/**
 * The unit-economics page's pure helpers: sorting and filtering the customer
 * rows, the months to choose from, the cost split, and how a flag or a piece
 * of evidence reads. Kept out of the components so they are tested alone.
 */
import type { CostBreakdown, EconomicsFlag, EconomicsRow, Evidence, UnitEconomicsResponse, Verdict } from '../../../common/models/unit-economics.model';

export type SortKey = 'name' | 'mrr' | 'cost' | 'margin' | 'marginPct' | 'influenced' | 'roi' | 'lastActive';
export type RowFilter = 'all' | 'paying' | 'trials' | 'flagged';

const valueOf = (r: EconomicsRow, key: SortKey): number | string | null => {
  switch (key) {
    case 'name':
      return r.name.toLowerCase();
    case 'mrr':
      return r.billing.mrrUsd;
    case 'cost':
      return r.costs.totalUsd;
    case 'margin':
      return r.marginUsd;
    case 'marginPct':
      return r.marginPct;
    case 'influenced':
      return r.value.influencedUsd;
    case 'roi':
      return r.value.roi;
    case 'lastActive':
      return r.usage.lastActiveAt ? new Date(r.usage.lastActiveAt).getTime() : null;
    default:
      return null;
  }
};

/** Sorted by `key`; a row with no value for it (no revenue on record, no shop) always goes last. */
export function sortRows(rows: EconomicsRow[], key: SortKey, desc: boolean): EconomicsRow[] {
  return [...rows].sort((a, b) => {
    const x = valueOf(a, key);
    const y = valueOf(b, key);
    if (x == null && y == null) return a.name.localeCompare(b.name);
    if (x == null) return 1;
    if (y == null) return -1;
    const c = x < y ? -1 : x > y ? 1 : a.name.localeCompare(b.name);
    return desc ? -c : c;
  });
}

export function filterRows(rows: EconomicsRow[], filter: RowFilter, search: string): EconomicsRow[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (needle && ![r.name, r.alias, r.plan.name].some((x) => String(x || '').toLowerCase().includes(needle))) return false;
    if (filter === 'paying') return r.billing.status === 'paying';
    if (filter === 'trials') return r.billing.status === 'trial' || r.billing.status === 'free';
    if (filter === 'flagged') return r.flags.length > 0;
    return true;
  });
}

/** The last `count` months, newest first, as 'YYYY-MM' (UTC). */
export function recentMonths(now: Date, count = 12): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

/** "October 2026" / "oct. 2026" in the language shown. */
export function monthLabel(month: string, locale: string, short = false): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Intl.DateTimeFormat(locale, { month: short ? 'short' : 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, 1)));
}

/** Only a loss carries colour; every other flag is a neutral mark. */
export const flagTone = (flag: EconomicsFlag): 'loss' | 'neutral' => (flag === 'loss_making' ? 'loss' : 'neutral');

/** Review asks for a look (amber); watch and keep are marks, not alarms. */
export const verdictTone = (v: Verdict): 'neutral' | 'info' | 'warning' => (v === 'review' ? 'warning' : v === 'watch' ? 'info' : 'neutral');

/** "12×" — sales influenced per dollar paid. */
export function formatMultiple(x: number | null | undefined): string {
  if (x == null || Number.isNaN(x)) return '—';
  return `${x >= 10 ? Math.round(x) : x.toFixed(1).replace(/\.0$/, '')}×`;
}

export type CostPart = 'ai' | 'generation' | 'seo' | 'gpu' | 'hosting' | 'unattributed';

/** A vendor's cost, part by part, largest first, zeros dropped. */
export function costParts(c: CostBreakdown): { key: CostPart; value: number }[] {
  const parts: { key: CostPart; value: number }[] = [
    { key: 'ai', value: c.aiUsd },
    { key: 'generation', value: c.generationUsd },
    { key: 'seo', value: c.seoUsd },
    { key: 'gpu', value: c.gpuShareUsd },
    { key: 'hosting', value: c.hostingShareUsd },
  ];
  return parts.filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
}

/** The month's whole cost, by part, unattributed included (it is ours too). */
export function platformCostParts(data: UnitEconomicsResponse): { key: CostPart; value: number }[] {
  const sum = (pick: (c: CostBreakdown) => number) => data.rows.reduce((s, r) => s + pick(r.costs), 0);
  // Rows may leave demos out (scope); totals never do, so the shared parts come from the pools.
  const gpu = data.pools.find((p) => p.key === 'gpu')?.allocatedUsd ?? sum((c) => c.gpuShareUsd);
  const hosting = data.pools.find((p) => p.key === 'hosting')?.allocatedUsd ?? sum((c) => c.hostingShareUsd);
  const direct = data.totals.directCostUsd;
  const rowsDirect = sum((c) => c.directUsd) || 1;
  // The direct total spread over its parts in the rows' proportions.
  const scale = direct / rowsDirect;
  return [
    { key: 'ai' as CostPart, value: sum((c) => c.aiUsd) * scale },
    { key: 'generation' as CostPart, value: sum((c) => c.generationUsd) * scale },
    { key: 'seo' as CostPart, value: sum((c) => c.seoUsd) * scale },
    { key: 'gpu' as CostPart, value: gpu },
    { key: 'hosting' as CostPart, value: hosting },
    { key: 'unattributed' as CostPart, value: data.totals.unattributedCostUsd },
  ].filter((p) => p.value > 0.005);
}

/** The i18n key and values for one piece of the feature review's evidence. */
export function evidenceText(e: Evidence, formatDate: (iso: string) => string, money: (usd: number) => string): { key: string; values: Record<string, string | number> } {
  const values: Record<string, string | number> = { ...(e.values || {}) };
  if (typeof values.date === 'string') values.date = formatDate(values.date);
  if (typeof values.usd === 'number') values.usd = money(values.usd);
  if (typeof values.n === 'number') values.count = values.n;
  return { key: `UNIT_ECONOMICS.EVIDENCE.${e.code}`, values };
}
